import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { BACKEND_URL } from '@/lib/backendUrl';
import { ymGoal } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import CalcRegisterModal from '@/components/auth/CalcRegisterModal';
import {
  COMMENT_MAX, GUEST_NAME, THREAD_META, formatCommentDate, tenureHint,
} from '@/components/comments/commentUtils';

/**
 * Комментарии внизу главной и страниц результата калькуляторов.
 *
 * Лента одна на весь сайт: на любой странице показываются все комментарии,
 * у каждого серая подпись «из …» — ссылка на страницу, где его оставили.
 * thread = 'home' | 'natal' | 'horar' | … — страница, где стоит блок: новый
 * комментарий запоминает её как источник. Ключи — THREADS в
 * backend/routers/comments.py, названия и адреса — THREAD_META.
 * Гость видит комментарии и кнопку входа (то же окно регистрации одной
 * строкой, что у калькуляторов). Текст выводится как текст: ссылки не
 * кликабельны, HTML не исполняется. Email рядом с комментарием не бывает —
 * сервер его и не отдаёт.
 *
 * data-pdf-exclude — чтобы блок не попадал в PDF результата.
 * compact — без своих боковых отступов, когда блок стоит внутри колонки страницы.
 */
const PAGE = 10;

function Stars({ count, months }) {
  if (!count) return null;
  const hint = tenureHint(months);
  return (
    <span
      className="text-amber-400 text-xs tracking-tight cursor-help select-none"
      title={hint}
      aria-label={hint}
      data-testid="comment-stars"
    >
      {'★'.repeat(count)}
    </span>
  );
}

function Avatar({ src, name, owner }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="w-9 h-9 rounded-full object-cover shrink-0 border border-slate-200"
        loading="lazy"
      />
    );
  }
  const letter = (name || GUEST_NAME).trim().charAt(0).toUpperCase() || '★';
  return (
    <div
      className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold ${
        owner ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
      }`}
      aria-hidden="true"
    >
      {letter}
    </div>
  );
}

function SourceLink({ thread }) {
  const meta = THREAD_META[thread];
  if (!meta) return null;
  return (
    <Link
      to={meta.path}
      className="text-xs text-slate-400 hover:text-slate-600 underline-offset-2 hover:underline"
      data-testid="comment-source"
    >
      из «{meta.label}»
    </Link>
  );
}

function CommentCard({ c, reply = false }) {
  const owner = !!c.is_owner_reply;
  return (
    <div
      className={`flex gap-3 rounded-xl p-3 sm:p-4 ${
        owner ? 'bg-violet-50 border border-violet-200' : 'bg-white border border-slate-200'
      } ${reply ? 'mt-2' : ''}`}
      data-testid={owner ? 'comment-owner-reply' : 'comment-item'}
    >
      <Avatar src={c.avatar} name={c.author_name} owner={owner} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {owner ? (
            <span className="text-xs font-semibold text-white bg-violet-600 rounded-full px-2 py-0.5">
              {c.badge || 'Светлана · автор сайта'}
            </span>
          ) : (
            <>
              <span className="text-sm font-semibold text-slate-800 break-words">
                {c.author_name || GUEST_NAME}
              </span>
              <Stars count={c.stars} months={c.months} />
            </>
          )}
          <span className="text-xs text-slate-400">{formatCommentDate(c.created_at)}</span>
          {!reply && <SourceLink thread={c.thread} />}
        </div>
        <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
          {c.text}
        </p>
      </div>
    </div>
  );
}

export default function CommentsBlock({ thread, compact = false, className = '' }) {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);
  const sendingRef = useRef(false);

  const load = useCallback(async (skip) => {
    const r = await fetch(`${BACKEND_URL}/api/comments/${encodeURIComponent(thread)}?skip=${skip}&limit=${PAGE}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [thread]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    load(0)
      .then((data) => {
        if (!alive) return;
        setItems(data.items || []);
        setHasMore(!!data.has_more);
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [load]);

  const showMore = async () => {
    setLoadingMore(true);
    try {
      const data = await load(items.length);
      const known = new Set(items.map((c) => c.id));
      setItems((prev) => [...prev, ...(data.items || []).filter((c) => !known.has(c.id))]);
      setHasMore(!!data.has_more);
    } catch (_e) {
      toast.error('Не удалось загрузить комментарии');
    } finally {
      setLoadingMore(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${BACKEND_URL}/api/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ thread, text: value }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 401) {
          setError('Сессия закончилась — войди ещё раз');
          setLoginOpen(true);
        } else {
          setError(typeof data.detail === 'string' ? data.detail : 'Не получилось отправить, попробуй ещё раз');
        }
        return;
      }
      setItems((prev) => [data, ...prev]);
      setText('');
      ymGoal('comment_posted', { thread });
      toast.success('Спасибо! Комментарий опубликован');
    } catch (_e) {
      setError('Нет связи с сервером, попробуй ещё раз');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const loggedIn = isAuthenticated && !!user;

  return (
    <section
      className={`w-full max-w-3xl mx-auto text-left ${compact ? 'pt-8' : 'px-4 py-10'} ${className}`}
      data-pdf-exclude
      data-testid={`comments-block-${thread}`}
    >
      <div className="rounded-2xl border border-sky-100 bg-gradient-to-b from-sky-50 to-white p-4 sm:p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
            <MessageCircle className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-heading font-semibold text-slate-900">
              Помоги нам стать лучше
            </h2>
            <p className="text-sm text-slate-500">Напиши, что понравилось, а чего не хватило</p>
          </div>
        </div>

        {loggedIn ? (
          <form onSubmit={submit} className="mb-5" data-testid="comment-form">
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value.slice(0, COMMENT_MAX)); setError(''); }}
              maxLength={COMMENT_MAX}
              rows={3}
              placeholder="Твой отзыв…"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-y"
              data-testid="comment-input"
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="text-xs text-slate-400">{text.length}/{COMMENT_MAX}</span>
              <Button
                type="submit"
                disabled={sending || !text.trim()}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="comment-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Отправить
              </Button>
            </div>
            {error && <p className="mt-2 text-sm text-rose-600" data-testid="comment-error">{error}</p>}
          </form>
        ) : (
          <div className="mb-5">
            <Button
              type="button"
              onClick={() => setLoginOpen(true)}
              className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="comment-login-btn"
            >
              Войди в один клик, чтобы оставить отзыв
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">Пока нет отзывов — будь первой!</p>
        ) : (
          <div className="space-y-3" data-testid="comments-list">
            {items.map((c) => (
              <div key={c.id}>
                <CommentCard c={c} />
                {(c.replies || []).length > 0 && (
                  <div className="ml-6 sm:ml-12">
                    {c.replies.map((r) => <CommentCard key={r.id} c={r} reply />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {hasMore && !loading && (
          <div className="mt-4 text-center">
            <Button variant="outline" onClick={showMore} disabled={loadingMore} data-testid="comments-more">
              {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Показать ещё
            </Button>
          </div>
        )}
      </div>

      <CalcRegisterModal
        place="comments"
        open={loginOpen && !loggedIn}
        onClose={() => setLoginOpen(false)}
        onRegistered={() => setTimeout(() => setLoginOpen(false), 1200)}
        title="Войди в один клик, чтобы оставить отзыв"
        submitLabel="Войти"
      />
    </section>
  );
}
